import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGooglePlay, faTelegram } from "@fortawesome/free-brands-svg-icons";
import { useLanguage } from "../../../../contexts/LanguageContext";
import "./DownloadDropdown.css";

const DownloadDropdown = ({ buttonText, telegramButtonText }) => {
  const { language } = useLanguage();
  const [statusRequest, setStatusRequest] = useState(0); // 0: idle, 1: loading, 2: downloaded
  const [showManualOptions, setShowManualOptions] = useState(false);

  useEffect(() => {
    // Detect Meta (Facebook / Instagram) internal webviews
    const isMetaBrowser = () => {
      const ua = navigator.userAgent || "";
      return (
        ua.includes("FBAN") ||
        ua.includes("FBAV") ||
        ua.includes("Instagram") ||
        ua.includes("FB_IAB")
      );
    };

    const isAndroid = /android/i.test(navigator.userAgent || "");

    // On Android, in-app browsers often block direct .apk downloads
    if (isMetaBrowser() && isAndroid) {
      alert(
        language === "ar"
          ? "أنت تستخدم متصفح فيسبوك / إنستغرام الداخلي. لتحميل التطبيق، يرجى فتح الصفحة في متصفح خارجي مثل Chrome."
          : "You are using Facebook/Instagram internal browser. To download the app, please open this page in an external browser like Chrome."
      );
    }
  }, [language]);

  const getTrackingParams = () => {
    const search = window.location.search || "";
    const urlParams = new URLSearchParams(search);

    // 1. Direct query parameter lookup
    let source =
      urlParams.get("source") ||
      urlParams.get("utm_source") ||
      urlParams.get("src");

    let extraParam1 =
      urlParams.get("f_sub_source") ||
      urlParams.get("utm_medium") ||
      urlParams.get("sub_source");

    let extraParam2 =
      urlParams.get("s_sub_source") ||
      urlParams.get("utm_campaign") ||
      urlParams.get("campaign");

    // 2. Backward compatibility with positional or key-less query strings (e.g. ?facebook&ad1&ad2)
    if (!source && search.length > 1) {
      const rawParams = search.slice(1).split("&");
      const cleanParams = [];

      for (const param of rawParams) {
        if (!param) continue;
        const [key, value] = param.split("=");
        const cleanKey = key.toLowerCase();
        // Skip common ad-tracking query hashes
        if (["fbclid", "gclid", "ttclid", "_ga", "igshid"].includes(cleanKey)) {
          continue;
        }
        cleanParams.push(value !== undefined ? value : key);
      }

      if (cleanParams.length > 0) {
        source = cleanParams[0];
        if (cleanParams[1] && !extraParam1) extraParam1 = cleanParams[1];
        if (cleanParams[2] && !extraParam2) extraParam2 = cleanParams[2];
      }
    }

    // 3. Fallback: Auto-detect from User Agent or Referrer (for Facebook & Instagram unified link)
    if (!source) {
      const ua = (navigator.userAgent || "").toLowerCase();
      const ref = (document.referrer || "").toLowerCase();

      if (ua.includes("instagram") || ref.includes("instagram.com")) {
        source = "instagram";
      } else if (
        ua.includes("fban") ||
        ua.includes("fbav") ||
        ref.includes("facebook.com")
      ) {
        source = "facebook";
      } else {
        source = "main_site";
      }
    }

    return {
      source: source || "main_site",
      extraParam1: extraParam1 || "",
      extraParam2: extraParam2 || "",
    };
  };

  const downloadInstaller = async () => {
    setStatusRequest(1); // loading

    const { source, extraParam1, extraParam2 } = getTrackingParams();

    try {
      const res = await axios.get(
        "https://cms.laftah.sss-sy.net/appversion/api/v1/download",
        {
          params: {
            source: source,
            f_sub_source: extraParam1,
            s_sub_source: extraParam2,
          },
        }
      );

      if (res.status === 200 || res.status === 201) {
        const fileUrl = res.data["file_url"];
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setStatusRequest(2);
      } else if (res.data.message !== "No version available for download.") {
        setStatusRequest(0);
        toast.error(
          language === "ar"
            ? "لا يوجد إصدار متاح للتنزيل"
            : "No version available for download."
        );
      } else if (res.data.error !== null) {
        setStatusRequest(0);
        toast.error(language === "ar" ? "❌ حدث خطأ ما" : "❌ Something went wrong");
      }
    } catch (err) {
      setStatusRequest(0);
      toast.error(language === "ar" ? "❌ حدث خطأ ما" : "❌ Something went wrong");
    }
  };

  const downloadFile = async ({ architecture } = {}) => {
    const { source, extraParam1, extraParam2 } = getTrackingParams();

    try {
      const res = await axios.get(
        "https://cms.laftah.sss-sy.net/appversion/api/v1/download",
        {
          params: {
            file_version: architecture,
            source: source,
            f_sub_source: extraParam1,
            s_sub_source: extraParam2,
          },
        }
      );

      if (res.status === 200 || res.status === 201) {
        const fileUrl =  res.data["file_url"];
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else if (res.data.message !== "No version available for download.") {
        toast.error(
          language === "ar"
            ? "لا يوجد إصدار متاح للتنزيل"
            : "No version available for download."
        );
      } else if (res.data.error !== null) {
        toast.error(language === "ar" ? "❌ حدث خطأ ما" : "❌ Something went wrong");
      }
    } catch (err) {
      toast.error(language === "ar" ? "❌ حدث خطأ ما" : "❌ Something went wrong");
    }
  };

  const defaultAndroidText =
    language === "ar"
      ? "تحميل مباشر لأجهزة Android"
      : "Direct download for Android";

  const defaultTelegramText =
    language === "ar"
      ? "عبر تيليجرام لأجهزة IPhone"
      : "Via Telegram for IPhone";

  return (
    <div className={`download-section ${language === "ar" ? "rtl" : "ltr"}`}>
      {!showManualOptions ? (
        <div className="download-buttons-group">
          {/* Android Download Button */}
          <button
            type="button"
            className={`download-btn android-btn ${
              statusRequest === 1 ? "loading" : ""
            } ${statusRequest === 2 ? "disabled" : ""}`}
            onClick={downloadInstaller}
            disabled={statusRequest === 1 || statusRequest === 2}
            aria-label="Direct download for Android"
          >
            {statusRequest === 0 && (
              <>
                <FontAwesomeIcon icon={faGooglePlay} className="btn-icon" />
                <span className="btn-title">
                  {buttonText || defaultAndroidText}
                </span>
              </>
            )}

            {statusRequest === 1 && (
              <div className="btn-status-content">
                <span className="spinner"></span>
                <span>{language === "ar" ? "جاري التجهيز..." : "Preparing..."}</span>
              </div>
            )}

            {statusRequest === 2 && (
              <div className="btn-status-content">
                <span className="checkmark">✔</span>
                <span>{language === "ar" ? "جاري التحميل..." : "Downloading..."}</span>
              </div>
            )}
          </button>

          {/* iOS / Telegram Button */}
          <a
            href="https://t.me/LaftahBBot"
            target="_blank"
            rel="noopener noreferrer"
            className="download-btn ios-telegram-btn"
            aria-label="Via Telegram for iPhone"
          >
            <FontAwesomeIcon icon={faTelegram} className="btn-icon" />
            <span className="btn-title">
              {telegramButtonText || defaultTelegramText}
            </span>
          </a>
        </div>
      ) : (
        <div className="manual-download">
          <p className="download-parh">
            {language === "ar"
              ? "اختر الإصدار المناسب لجهازك:"
              : "Select the appropriate version:"}
          </p>
          <div className="download-buttons">
            <button onClick={() => downloadFile({ architecture: "v7a" })}>
              📱 32-bit version
            </button>
            <button onClick={() => downloadFile({ architecture: "v8a" })}>
              📱 64-bit version
            </button>
          </div>
          <button
            className="back-button"
            onClick={() => setShowManualOptions(false)}
          >
            {language === "ar" ? "رجوع" : "Back"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DownloadDropdown;
